import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getActiveProjectId } from '../utils/session';
import { resolveRecordRef, type ResolvedCandidate } from '../services/recordRefResolver';
import { useRouteResolution } from './RefRouter';
import { claimResolution, setResolution } from './routeResolution';

export interface RouteRefState {
  /** The page cannot open yet: another project is being opened, or a short address is being looked up. */
  blocking: boolean;
  /** The address names a project this account cannot open. */
  projectUnavailable: boolean;
  /** The project could not be switched to (offline, refused). */
  switchFailed: boolean;
  /** Two records share the typed reference: show them and let the person pick. */
  ambiguous: { segment: string; candidates: ResolvedCandidate[] } | null;
}

/**
 * Finishes what the router could not do from memory, once the person is signed in:
 *  1. `/project/PJ-x/...` for another project: open that project (reloads the app on the same address);
 *  2. `PO-130` / `PO-90F0D6B4` that the loaded numbers do not know: ask the API.
 * Both only ever add information; a failed lookup leaves the address as typed, so the page says "not found" itself.
 */
export function useRouteRefResolution(): RouteRefState {
  const { isAuthenticated, workspaces = [], switchWorkspace } = useAuth();
  const { pending, ambiguous, urlProjectHex, projectMismatch } = useRouteResolution();
  const [switchFailed, setSwitchFailed] = useState(false);
  const switching = useRef(false);

  const target = projectMismatch && urlProjectHex
    ? workspaces.find(w => w.project_id.toLowerCase().startsWith(urlProjectHex))
    : undefined;
  const projectUnavailable = isAuthenticated && projectMismatch && !target;

  useEffect(() => {
    if (!isAuthenticated || !target || switching.current) return;
    switching.current = true;
    switchWorkspace(target.project_id).catch(() => { switching.current = false; setSwitchFailed(true); });
  }, [isAuthenticated, target, switchWorkspace]);

  const refKey = pending.map(p => p.segment).join('|');
  useEffect(() => {
    const projectId = getActiveProjectId();
    if (!isAuthenticated || projectMismatch || !projectId) return;
    for (const { segment } of pending) {
      if (!claimResolution(projectId, segment)) continue;
      resolveRecordRef(projectId, segment).then(outcome => {
        if (outcome.status === 'found') setResolution(projectId, segment, { status: 'found', id: outcome.id });
        else if (outcome.status === 'ambiguous') setResolution(projectId, segment, { status: 'ambiguous', candidates: outcome.candidates });
        else setResolution(projectId, segment, { status: outcome.status === 'notFound' ? 'notFound' : 'error' });
      });
    }
    // refKey stands for `pending`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, projectMismatch, refKey]);

  const blocking = isAuthenticated && !projectUnavailable && !switchFailed && (projectMismatch || pending.length > 0);
  return { blocking, projectUnavailable, switchFailed, ambiguous };
}
